import {
  ChangeDetectorRef,
  Component,
  OnInit,
  SecurityContext,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { InceptionService, TranslationService } from '@dch/ng-common';
import {
  getBillingTransactionsRequest,
  getCallDetailsRequest,
} from 'src/app/state/actions';
import { ApplicationState } from 'src/app/state/reducers';
import { CallDetailsState } from 'src/app/state/reducers/call-details.reducer';
import { selectCallDetails } from 'src/app/state/selectors/call-details.selectors';
import { CsvParseResult } from '../../models/csv-parser.model';
import {
  parseCsvFile,
  COLUMN_INDEXES,
} from '../../services/csv-parser.service';
import { getPager } from '../../services/pager.service';
import { Pager } from '../../models/pager.model';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { CustomerAccount } from '@dch/common-business-logic';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import {
  getBufferedArrayFor,
  saveDocument,
} from 'src/app/my-bill/services/save-document.service';
import { filter, tap } from 'rxjs/operators';
import { selectTransactionsByAccountNumber } from 'src/app/state/selectors/billing-transactions.selectors';
import { isDocumentLesserThan12MonthsOld } from 'src/app/my-bill/services/utils.service';
import {
  IBillingDocument,
  IBillingTransactions,
} from '@dch/billing-business-logic';

@Component({
  selector: 'my-bill-call-details-table',
  templateUrl: './call-details-table.component.html',
  standalone: false,
})
export class CallDetailsTableComponent implements OnInit {
  // Note: To expose enum to the template.
  readonly COLUMN_INDEXES = COLUMN_INDEXES;
  readonly FORWARD_TO_VOICE_MAIL_CODES = [
    "'1961'",
    "'1962'",
    "'1963'",
    "'1964'",
    "'1965'",
  ];
  customerAccount?: CustomerAccount;
  callDetails?: CallDetailsState;
  parseResult?: CsvParseResult;
  selectedPhoneNumber = '';
  selectedCallCategory = '';
  selectedDate = '';
  selectedAmountFilter = '';
  rowLimit = 5;
  selectedPageNumber = 1;
  numberOfPages = 2;
  pager: Pager = { currentPage: 1 } as any;
  rows?: Array<Array<string>>;
  pagedRows?: Array<Array<string>>;
  filteredRows?: Array<Array<string>>;
  faqHtml?: string;
  private billingAccountNumber?: string;
  private documentId?: string;
  errorTitleTranslationKey?: string;
  errorContentTranslationKey?: string;
  is12MonthOldDocument = false;

  get hasError(): boolean {
    return (
      (this.callDetails?.hasError ?? false) ||
      (this.parseResult?.hasError ?? false)
    );
  }
  get errorCode(): 3006 | 3007 | 3008 | null {
    if (
      this.callDetails?.errorCode === 3006 ||
      this.callDetails?.errorCode === 3007 ||
      this.callDetails?.errorCode === 3008
    ) {
      return this.callDetails?.errorCode;
    }

    return null;
  }
  get vatLabel(): string | undefined {
    let vatLabel: string | undefined;
    if (this.customerAccount) {
      vatLabel = this.translationService.getTranslatedValueFor(
        this.customerAccount.isRES()
          ? 'bills.callDetails.pricesIncludeVatLabel'
          : 'bills.callDetails.pricesExcludeVatLabel'
      );
    }
    return vatLabel;
  }

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private inceptionService: InceptionService
  ) {}

  ngOnInit() {
    this.fetchTransactions();

    this.store
      .select(selectTransactionsByAccountNumber(this.billingAccountNumber))
      .pipe(
        filter((transactions) => transactions && transactions.result !== null),
        tap((transactions) => {
          if (this.isDocumentLesserThan12Months(transactions.result)) {
            this.store.dispatch(
              getCallDetailsRequest({
                billingAccountNumber: this.billingAccountNumber,
                documentId: this.documentId,
              })
            );
          } else {
            this.is12MonthOldDocument = true;
            this.errorTitleTranslationKey =
              'bills.cover.usageLevel4.documentOlderThan12Months.title';
            this.errorContentTranslationKey =
              'bills.cover.usageLevel4.documentOlderThan12Months.message';
          }
        })
      )
      .subscribe();

    this.store.select(selectCallDetails).subscribe((callDetails) => {
      this.callDetails = callDetails;

      if (this.hasError) {
        switch (this.errorCode) {
          case 3006:
          case 3007:
          case 3008:
            this.errorTitleTranslationKey = `bills.callDetails.errorCode${this.errorCode}Title`;
            this.errorContentTranslationKey = `bills.callDetails.errorCode${this.errorCode}`;
            break;
          default:
            this.errorTitleTranslationKey = 'bills.callDetails.errorTitle';
            this.errorContentTranslationKey =
              'bills.callDetails.noUsageGeneratedLabel';
            break;
        }
      }

      if (callDetails.callDetails !== null) {
        this.parseResult = parseCsvFile(callDetails.callDetails);
        if (this.parseResult.hasError === false) {
          this.rows = this.parseResult.rows;
          this.pagedRows = this.parseResult.rows;
          this.filteredRows = this.parseResult.rows;

          this.setPage(1);
        }
      }
    });

    const url = 'call-details-faq%7EmgnlArea=body%7E.html';
    this.http.get(url, { responseType: 'text' }).subscribe((response) => {
      this.faqHtml = this.sanitizer.sanitize(SecurityContext.HTML, response);
    });
    this.scrollToTop();
  }

  onFilterValueSelected(applyFilter: boolean) {
    this.filteredRows = [...this.rows];
    if (applyFilter) {
      this.filterRowsWithValue(
        COLUMN_INDEXES.CHARGED_NUMBER,
        this.selectedPhoneNumber
      );
      this.filterRowsWithValue(
        COLUMN_INDEXES.CALL_CATEGORY,
        this.selectedCallCategory
      );
      this.filterRowsWithValue(COLUMN_INDEXES.DATE, this.selectedDate);
      this.filterRowsWithValue(
        COLUMN_INDEXES.AMOUNT,
        this.selectedAmountFilter,
        true
      );
    } else {
      this.selectedPhoneNumber = '';
      this.selectedCallCategory = '';
      this.selectedDate = '';
      this.selectedAmountFilter = '';
    }
    this.setPage(1);
  }

  hasOneFilterActive(): boolean {
    return (
      this.selectedPhoneNumber !== '' ||
      this.selectedCallCategory !== '' ||
      this.selectedDate !== '' ||
      this.selectedAmountFilter !== ''
    );
  }

  downloadCsv() {
    const { billingAccountNumber, documentId } = this;
    if (this.callDetails.callDetails !== null) {
      saveDocument(
        getBufferedArrayFor(this.callDetails.callDetails),
        documentId + '.csv',
        'text/csv'
      );
    }
  }

  printCsv() {
    this.rowLimit = 9999;
    this.setPage(1);
    this.changeDetectorRef.detectChanges();

    const printableDiv =
      window.document.getElementById('printableDiv').outerHTML;
    const links = Array.from(window.document.getElementsByTagName('link'))
      .map((link) => link.outerHTML)
      .join('');

    const html = `
    <html>
      <head>
        <title>Bill</title>
        ${links}
      </head>
      <body>
        <div>${printableDiv}</div>
        <script>
          document.addEventListener(
            'readystatechange',
            function () {
              if (document.readyState == 'complete') {
                window.print();
                window.close();
              }
            }
          );
        </script>
      </body>
    </html>
    `;
    const printWindow = window.open();
    printWindow.document.write(html);
    printWindow.document.close();

    this.rowLimit = 5;
    this.setPage(1);
  }

  setPage(page: number) {
    this.pager = getPager(this.filteredRows.length, page, this.rowLimit);
    this.pagedRows = this.filteredRows.slice(
      this.pager.startIndex,
      this.pager.endIndex + 1
    );
    this.numberOfPages = Math.ceil(this.filteredRows.length / this.rowLimit);
  }

  handlePageNumberChange() {
    if (this.selectedPageNumber > this.numberOfPages) {
      this.selectedPageNumber = this.numberOfPages;
    } else if (this.selectedPageNumber < 1) {
      this.selectedPageNumber = 1;
    }
    this.setPage(this.selectedPageNumber);
  }

  columnIsNotEmpty(columnIndex: number) {
    return (
      this.pagedRows?.some((row) => this.isDefined(row[columnIndex])) ?? false
    );
  }

  isValueContainedInForwardToVoiceMailCodes(value: string) {
    return this.FORWARD_TO_VOICE_MAIL_CODES.includes(value);
  }

  getTranslatedLabelForForwardToVoiceMailCode(value: string) {
    let translationKey: string;
    switch (value) {
      case "'1961'":
        translationKey = 'bills.callDetails.forwardToVoiceMailLabelEn';
        break;
      case "'1962'":
        translationKey = 'bills.callDetails.forwardToVoiceMailLabelNl';
        break;
      case "'1963'":
        translationKey = 'bills.callDetails.forwardToVoiceMailLabelFr';
        break;
      case "'1964'":
        translationKey = 'bills.callDetails.forwardToVoiceMailLabelDe';
        break;
      default:
        translationKey = 'bills.callDetails.forwardToVoiceMailLabelEn';
        break;
    }
    return this.translationService.getTranslatedValueFor(translationKey);
  }

  private isDocumentLesserThan12Months(
    billingTransaction: IBillingTransactions
  ) {
    let billingDocument = this.findDocument([
      ...billingTransaction.openTransactions.billingDocuments,
      ...billingTransaction.recentClosedTransactions,
    ]);
    return (
      document && isDocumentLesserThan12MonthsOld(billingDocument.documentDate)
    );
  }

  private findDocument(billingDocuments: IBillingDocument[]) {
    return billingDocuments.find(
      (billDocument) => billDocument.reference === this.documentId
    );
  }

  private fetchTransactions() {
    this.route.params
      .pipe(
        filter(
          ({ billingAccountNumber, documentId }) =>
            billingAccountNumber !== undefined && documentId !== undefined
        ),
        tap(({ billingAccountNumber, documentId }) => {
          this.billingAccountNumber = billingAccountNumber;
          this.documentId = documentId;
          this.store.dispatch(
            getBillingTransactionsRequest(billingAccountNumber)
          );
        })
      )
      .subscribe();
  }

  private filterRowsWithValue(
    columnIndex: number,
    filterValue?: string,
    isFilterAmount: boolean = false
  ) {
    if (filterValue) {
      this.filteredRows = this.filteredRows.filter((row) =>
        this.rowPassesFilter(row, columnIndex, filterValue, isFilterAmount)
      );
    }
  }

  private rowPassesFilter(
    row: string[],
    columnIndex: number,
    filterValue: string,
    isFilterAmount: boolean
  ) {
    let match = false;
    let value = row[columnIndex];
    if (value) {
      if (value.charAt(0) === "'") {
        value = value.substring(1);
      }
      if (value.charAt(value.length - 1) === "'") {
        value = value.substring(0, value.length - 1);
      }
      if (isFilterAmount) {
        let amount = Number(value.replace(',', '.'));
        match =
          this.selectedAmountFilter === 'zero' ? amount === 0 : amount !== 0;
      } else {
        match = value === filterValue;
      }
    }
    return match;
  }

  private isDefined(value: string): boolean {
    return Boolean(value) && value.trim() !== '';
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.inceptionService.scrollToElement('#calldetailsTop');
    });
  }
}
